import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  signal,
  ViewChild,
  WritableSignal,
  input,
  inject,
} from '@angular/core';
import { ControlValueAccessor, FormControl, FormsModule, NgControl, ReactiveFormsModule } from '@angular/forms';
import { DateAdapter } from '@angular/material/core';
import { MatDatepicker, MatDatepickerInputEvent, MatDatepickerModule } from '@angular/material/datepicker';

import { IconComponent } from '@app-shared/components/icon/icon.component';
import { ControlDateFormatter, IComplexDate, YamahaDateAdapter } from '@app-shared/utils';
import { fromEvent, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'app-datepicker-control',
  templateUrl: 'datepicker-control.component.html',
  styleUrls: ['./datepicker-control.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, MatDatepickerModule, ReactiveFormsModule, IconComponent],
  providers: [{ provide: DateAdapter, useClass: YamahaDateAdapter }],
})
export class DatepickerControlComponent implements ControlValueAccessor, AfterViewInit, OnDestroy {
  private cdr = inject(ChangeDetectorRef);
  control = inject(NgControl, { self: true, optional: true });

  @ViewChild('input', { read: ElementRef })
  set input(el: ElementRef) {
    this._input = el;
    this._input.nativeElement.value = this._value ? this._value.formatted : '';
  }

  get input(): ElementRef {
    return this._input;
  }

  readonly label = input('');
  readonly placeholder = input.required<string>();
  @Input() @HostBinding('class.ui-inline-view') inlineView: boolean = false;

  @Input({ required: true }) set dateRange(dayRange: [number, number]) {
    if (dayRange) {
      this.minDate = this._addDays(-dayRange[0]);
      this.maxDate = this._addDays(dayRange[1]);
    }
  }

  date: FormControl<Date | string> = new FormControl();
  disabled: WritableSignal<boolean> = signal(false);
  minDate: Date = this._addDays(-36500);
  maxDate: Date = new Date();
  errorMessage: string = '';

  private _value: IComplexDate | null;
  private _input: ElementRef;
  private _destroy$: Subject<void> = new Subject<void>();

  constructor() {
    if (this.control) {
      this.control.valueAccessor = this;
    }
  }

  @HostListener('focusout') onTouched(): void {
    this._onTouched();
  }

  ngAfterViewInit(): void {
    fromEvent(this.input.nativeElement, 'keyup')
      .pipe(
        distinctUntilChanged(),
        debounceTime(500),
        filter(() => this.input.nativeElement.value.length === 10),
        tap(() => this._updateValue(this.input.nativeElement.value)),
        takeUntil(this._destroy$)
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  get invalid(): boolean {
    return this.control ? this.control.invalid && (this.control.dirty || this.control.touched) : false;
  }

  get showError(): boolean {
    if (!this.control) {
      return false;
    }

    const { dirty, touched, errors } = this.control;
    if (errors?.errorMessage) {
      this.errorMessage = errors.errorMessage;
    }

    return this.invalid;
  }

  writeValue(value: IComplexDate): void {
    this._value = value;
    this.date.setValue(value ? new Date(value.jsdate) : '', { emitEvent: false });
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (string: unknown) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  datepickerDateChange(date: MatDatepickerInputEvent<Date, Date | null>): void {
    const dataJson = date.targetElement['value'];
    if (!this._value || dataJson !== this._value.formattedUS) {
      this._updateValue(dataJson);
    }
  }

  openDatePicker(picker: MatDatepicker<unknown>): void {
    if (!this.disabled()) {
      picker.open();
    }
  }

  clear(event: MouseEvent) {
    event.stopPropagation();
    this._updateValue('');
  }

  private _updateValue(val: string): void {
    const value = ControlDateFormatter.encode(val);
    this.writeValue(value);
    this._onChange(value);
    this._onTouched();
  }

  private _onChange: (value: IComplexDate) => void = () => undefined;
  private _onTouched: () => void = () => undefined;

  private _addDays(days: number): Date {
    const date = new Date();
    date.setDate(date.getDate() + days);

    return date;
  }
}
